import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../context/UserContext';
import { convertUrlsToMarkdownLinks } from '../utils/textUtils';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  TextField,
  Typography,
  Paper,
  IconButton,
  Button,
  CircularProgress,
  Stack,
  Container,
  Snackbar,
  Alert,
  useTheme
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import SaveIcon from '@mui/icons-material/Save';
import { useSelectedChatbot } from '../context/SelectedChatbotContext';
import { fetchTextData, sendChatMessage, saveInstructions, fetchChatbotData } from '../services/api';

// Componente para manejar errores silenciosamente
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: false }; // No mostrar UI de error
  }

  componentDidCatch(error, errorInfo) {
    // Solo registrar en consola si no es un error de ResizeObserver
    if (!error.message?.includes('ResizeObserver')) {
      console.error('Error:', error);
      console.error('Error Info:', errorInfo);
    }
  }

  render() {
    return this.props.children;
  }
}

function Training() {
  const theme = useTheme();
  const { user } = useUser();
  const isReadOnly = user?.rol === 'readonly';
  const [instructions, setInstructions] = useState('');
  const [currentMessage, setCurrentMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState(null);
  const [originalInstructions, setOriginalInstructions] = useState('');
  const [isModified, setIsModified] = useState(false);
  const [chatId, setChatId] = useState(null);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [exceedsLimit, setExceedsLimit] = useState(false);
  const { selectedChatbot } = useSelectedChatbot();
  const messagesEndRef = useRef(null);

  // Obtener el límite de caracteres personalizado si existe en las opciones del producto
  const MAX_CHAR_LIMIT = (() => {
    // Valor por defecto
    const DEFAULT_LIMIT = 75000;
    
    // Buscar el producto seleccionado
    const selectedProduct = user?.products?.find(p => p.code === selectedChatbot);
    
    // Extraer el tamaño de instrucciones personalizado de las opciones si existe
    if (selectedProduct?.options) {
      // Buscar INSTRUCTIONS_SIZE= en cualquier posición de la cadena
      const sizeMatch = selectedProduct.options.match(/INSTRUCTIONS_SIZE=(\d+)/i);
      if (sizeMatch && sizeMatch[1]) {
        const customLimit = parseInt(sizeMatch[1]);
        if (!isNaN(customLimit) && customLimit > 0) {
          return customLimit;
        }
      }
    }
    
    return DEFAULT_LIMIT;
  })();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Efecto para hacer scroll cuando hay nuevos mensajes
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Cargar mensaje de bienvenida
  useEffect(() => {
    const loadWelcome = async () => {
      if (!selectedChatbot) return;

      try {
        const data = await fetchChatbotData(selectedChatbot);
        if (data?.welcome) {
          setMessages([{ role: 'assistant', content: data.welcome }]);
        }
      } catch (err) {
        console.error('Error loading welcome:', err);
      }
    };

    loadWelcome();
  }, [selectedChatbot]);

  // Cargar instrucciones
  useEffect(() => {
    const loadInstructions = async () => {
      if (!selectedChatbot) {
        setInstructions('');
        setOriginalInstructions('');
        setError(null);
        setIsModified(false);
        setCharCount(0);
        setChatId(''); // Reiniciar chatId para que la próxima conversación use las instrucciones actualizadas
        return;
      }

      try {
        setError(null);
        const data = await fetchTextData(selectedChatbot);

        if (!data || !data.text) {
          setError('No se encontraron instrucciones para este chatbot');
          return;
        }

        setInstructions(data.text);
        setOriginalInstructions(data.text);
        setCharCount(data.text.length);
        setExceedsLimit(data.text.length > MAX_CHAR_LIMIT);
        setIsModified(false);
        setChatId(''); // Reiniciar chatId para que la próxima conversación use las instrucciones actualizadas
      } catch (err) {
        console.error('Error loading instructions:', err);
        setError('Error al cargar las instrucciones del chatbot');
        setInstructions('');
        setOriginalInstructions('');
        setCharCount(0);
        setChatId(''); // Reiniciar chatId para que la próxima conversación use las instrucciones actualizadas
      }
    };

    loadInstructions();
  }, [selectedChatbot]);

  const handleInstructionsChange = (event) => {
    const newText = event.target.value;
    setInstructions(newText);
    setCharCount(newText.length);
    setExceedsLimit(newText.length > MAX_CHAR_LIMIT);
    setIsModified(newText !== originalInstructions);
    setChatId(''); // Reiniciar chatId para que la próxima conversación use las instrucciones actualizadas
  };

  const handleSaveChanges = async () => {
    if (isReadOnly) return;
    if (!selectedChatbot || !instructions.trim() || exceedsLimit) return;

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      await saveInstructions(selectedChatbot, instructions);
      setOriginalInstructions(instructions);
      setIsModified(false);
      setSaveSuccess(true);

      // Reiniciar chatId para que la próxima conversación use las instrucciones actualizadas
      setChatId('');
      // Limpiar mensajes anteriores ya que el contexto ha cambiado
      setMessages([]);

      // Cargar el nuevo mensaje de bienvenida
      try {
        const data = await fetchChatbotData(selectedChatbot);
        if (data?.welcome) {
          setMessages([{ role: 'assistant', content: data.welcome }]);
        }
      } catch (err) {
        console.error('Error loading welcome message:', err);
      }
    } catch (error) {
      setSaveError(error.message || 'Error al guardar los cambios');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendMessage = async () => {
    if (!currentMessage.trim() || !selectedChatbot) return;

    const messageText = currentMessage.trim();
    setCurrentMessage('');

    // Añadir mensaje del usuario
    const userMessage = { role: 'user', content: messageText };
    setMessages(prev => [...prev, userMessage]);

    // Añadir mensaje de "escribiendo..."
    const typingMessage = { role: 'assistant', content: 'Escribiendo...', isTyping: true };
    setMessages(prev => [...prev, typingMessage]);

    setIsSendingMessage(true);

    try {
      const response = await sendChatMessage(selectedChatbot, messageText, chatId);

      // Remover el mensaje de "escribiendo..."
      setMessages(prev => prev.filter(msg => !msg.isTyping));

      if (response && response.message) {
        const botMessage = { role: 'assistant', content: response.message };
        setMessages(prev => [...prev, botMessage]);

        if (response.chatId) {
          setChatId(response.chatId);
        }
      } else {
        throw new Error('El servidor no envió una respuesta válida');
      }
    } catch (error) {
      // Remover el mensaje de "escribiendo..."
      setMessages(prev => prev.filter(msg => !msg.isTyping));

      console.error('Error al enviar mensaje:', error);

      // Mostrar mensaje de error más amigable
      const errorMessage = {
        role: 'assistant',
        content: error.message || 'Lo siento, ha ocurrido un error al procesar tu mensaje. Por favor, inténtalo de nuevo más tarde.',
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <ErrorBoundary>
      <Container maxWidth={false} sx={{ py: 0 }}>
        <Stack spacing={2}>
          {/* Header */}
          <Paper
            elevation={3}
            sx={{
              p: { xs: 2.5, sm: 3 },
              mb: { xs: 0.5, sm: 1.5 },
              borderRadius: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <Typography variant="h3" component="h1" sx={{ color: theme.palette.text.primary, mb: 0.5, fontWeight: 800, letterSpacing: '-0.03em', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif', }}>
              Entrenamiento
            </Typography>
            <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontWeight: 500, fontSize: '1rem', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
              Configura las instrucciones del asistente y prueba su comportamiento en tiempo real
            </Typography>
          </Paper>

          {/* Instructions */}
          <Paper
            elevation={3}
            sx={{
              borderRadius: 2,
              mb: 2,
              height: '400px',
              minHeight: '200px',
              maxHeight: '900px',
              resize: 'vertical',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)'}`,
              boxShadow: theme.palette.mode === 'dark' ? '0 18px 40px rgba(0,0,0,0.20)' : '0 16px 36px rgba(15,23,42,0.07)'
            }}
          >
            <Box sx={{
              px: 2,
              py: 1.35,
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(15,23,42,0.02)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              zIndex: 1,
              backdropFilter: 'blur(8px)',
              minHeight: '52px'
            }}>
              <Typography variant="subtitle1" sx={{ color: theme.palette.text.primary, fontWeight: 700, letterSpacing: '-0.01em', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                Instrucciones del Asistente
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: exceedsLimit ? theme.palette.error.main : theme.palette.text.secondary,
                    fontFamily: 'monospace',
                    marginRight: '10px',
                    fontWeight: exceedsLimit ? 'bold' : 'normal',
                    fontSize: '0.78rem',
                    opacity: 0.9,
                  }}
                >
                  {charCount}/{MAX_CHAR_LIMIT} caracteres
                </Typography>
                {isModified && (
                  <Button
                    variant="contained"
                    onClick={isReadOnly ? undefined : handleSaveChanges}
                    startIcon={isSaving ? <CircularProgress size={16} sx={{ color: theme.palette.primary.contrastText }} /> : <SaveIcon sx={{ fontSize: '1.2rem' }} />}
                    disabled={isSaving || exceedsLimit || isReadOnly}
                    size="small"
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      height: '28px',
                      minHeight: '28px',
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                      },
                      '&.Mui-disabled': {
                        backgroundColor: theme.palette.action.hover,
                        color: theme.palette.text.secondary,
                      },
                      transition: 'all 0.2s ease-in-out',
                      marginRight: 1,
                      py: 0,
                      lineHeight: 1,
                      '& .MuiButton-startIcon': {
                        marginRight: 0.5,
                        marginLeft: 0.5
                      }
                    }}
                  >
                    {isSaving ? 'Guardando...' : exceedsLimit ? 'Límite excedido' : 'Guardar Cambios'}
                  </Button>
                )}
              </Box>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex' }}>
              {selectedChatbot ? (
                <Box sx={{ p: 2, height: '100%', width: '100%', display: 'flex', minHeight: 0 }}>
                  <Box
                    component="textarea"
                    value={instructions}
                    onChange={handleInstructionsChange}
                    disabled={isReadOnly}
                    spellCheck={false}
                    sx={{
                      flex: 1,
                      width: '100%',
                      height: '100%',
                      minHeight: 0,
                      resize: 'none',
                      border: 'none',
                      outline: 'none',
                      backgroundColor: 'transparent',
                      color: theme.palette.text.primary,
                      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                      fontSize: '0.92rem',
                      lineHeight: 1.7,
                      padding: '18px 18px 20px',
                      boxSizing: 'border-box',
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      scrollbarWidth: 'thin',
                      scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.22)' : 'rgba(15,23,42,0.20)'} transparent`,
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.22)' : 'rgba(15,23,42,0.20)',
                        borderRadius: '999px',
                      },
                    }}
                  />
                </Box>
              ) : (
                <Typography sx={{ p: 2, color: theme.palette.text.secondary }}>
                  Selecciona un asistente para ver sus instrucciones
                </Typography>
              )}
            </Box>
          </Paper>

          {/* Chat */}
          <Paper
            elevation={3}
            sx={{
              borderRadius: 2,
              height: '300px',
              minHeight: '200px',
              maxHeight: '800px',
              resize: 'vertical',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box sx={{
              p: 0.1,
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.action.hover,
              position: 'sticky',
              top: 0,
              zIndex: 1,
              backdropFilter: 'blur(8px)'
            }}>
              <Typography variant="h7" sx={{ color: theme.palette.text.primary, fontWeight: 400, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif', marginLeft: '10px' }}>
                Probar Asistente
              </Typography>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              <Stack spacing={1}>
                {messages.map((message, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <Paper
                      sx={{
                        maxWidth: '80%',
                        p: 1.5,
                        backgroundColor: message.role === 'user' ? theme.palette.primary.main : theme.palette.background.default,
                        color: message.role === 'user' ? theme.palette.primary.contrastText : theme.palette.text.primary,
                      }}
                    >
                      <Box sx={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif', color: 'inherit' }}>
                        <ReactMarkdown components={{ 
  p: ({ node, ...props }) => <p style={{ marginTop: '0.5em', marginBottom: '0.5em' }} {...props} />,
  a: ({ node, ...props }) => <a style={{ color: theme.palette.primary.main, textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer" {...props} />
}}>{convertUrlsToMarkdownLinks(message.content)}</ReactMarkdown>
                      </Box>
                    </Paper>
                  </Box>
                ))}
                <div ref={messagesEndRef} />
              </Stack>
            </Box>

            <Box sx={{
              p: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.action.hover,
              display: 'flex',
              gap: 1
            }}>
              <TextField
                fullWidth
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Escribe tu mensaje..."
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    '& fieldset': {
                      borderColor: theme.palette.divider,
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.light,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                }}
              />
              <IconButton
                onClick={handleSendMessage}
                disabled={!currentMessage.trim() || isSendingMessage}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  '&.Mui-disabled': {
                    backgroundColor: theme.palette.action.hover,
                    color: theme.palette.text.secondary,
                  }
                }}
              >
                {isSendingMessage ? (
                  <CircularProgress size={24} sx={{ color: theme.palette.primary.contrastText }} />
                ) : (
                  <SendIcon />
                )}
              </IconButton>
            </Box>
          </Paper>
        </Stack>
      </Container>

      {/* Notificaciones */}
      <Snackbar
        open={saveSuccess}
        autoHideDuration={3000}
        onClose={() => setSaveSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" sx={{ backgroundColor: theme.palette.success.main, color: theme.palette.getContrastText(theme.palette.success.main) }}>
          Cambios guardados correctamente
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!saveError}
        autoHideDuration={5000}
        onClose={() => setSaveError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="error" sx={{ backgroundColor: theme.palette.error.main, color: theme.palette.getContrastText(theme.palette.error.main) }}>
          {saveError}
        </Alert>
      </Snackbar>

      <Snackbar
        open={exceedsLimit && isModified}
        autoHideDuration={5000}
        onClose={() => {}}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="error" sx={{ backgroundColor: theme.palette.error.main, color: theme.palette.getContrastText(theme.palette.error.main) }}>
          Se ha superado el número máximo de caracteres.
        </Alert>
      </Snackbar>
    </ErrorBoundary>
  );
};

export default Training;
